import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { citasAPI, clientesAPI, authAPI } from '../services/api';
import CitaForm from './CitaForm';
import { getClienteColor as getBaseClienteColor } from '../utils/clienteColors';
import { safeHtml } from '../utils/sanitize';
import { exportToCSV } from '../utils/exportCSV';
import './Citas.css';
import './CalendarioVisual.css';

const CalendarioVisual = lazy(() => import('./CalendarioVisual'));

function Citas() {
  const location = useLocation();
  const [citas, setCitas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCita, setEditingCita] = useState(null);
  const [openEmailModalOnMount, setOpenEmailModalOnMount] = useState(false);
  const [fechaInicial, setFechaInicial] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [vistaCalendario, setVistaCalendario] = useState(true);
  const [vistaFullCalendar, setVistaFullCalendar] = useState(false);
  const [mesActual, setMesActual] = useState(new Date());

  const loadClientes = useCallback(async () => {
    try {
      const response = await clientesAPI.getAll(true);
      setClientes(response.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadCitas = useCallback(async () => {
    try {
      setLoading(true);
      const clienteId = filtroCliente ? parseInt(filtroCliente) : null;
      const response = await citasAPI.getAll(clienteId, fechaInicio || null, fechaFin || null);
      setCitas(response.data);
      setError(null);
    } catch (err) {
      setError('Error al cargar las citas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filtroCliente, fechaInicio, fechaFin]);

  useEffect(() => {
    authAPI.check().then(r => setCurrentUser(r.data?.user || r.data)).catch(() => {});
    loadClientes();
    loadCitas();
  }, [loadClientes, loadCitas]);

  useEffect(() => {
    loadCitas();
  }, [loadCitas]);

  useEffect(() => {
    const targetCitaId = location.state?.targetCitaId;
    if (!targetCitaId || citas.length === 0) return;

    setVistaCalendario(false);
    setVistaFullCalendar(false);

    const scrollAndHighlight = () => {
      const element = document.getElementById(`cita-${targetCitaId}`);
      if (!element) return;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const prevBg = element.style.backgroundColor;
      element.style.backgroundColor = '#fff3cd';
      setTimeout(() => {
        element.style.backgroundColor = prevBg;
      }, 1800);
      window.history.replaceState({}, document.title);
    };

    const t = setTimeout(scrollAndHighlight, 120);
    return () => clearTimeout(t);
  }, [location.state, citas]);

  const getClienteNombre = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? `${cliente.nombre} ${cliente.apellidos}` : 'Cliente no encontrado';
  };

  const getClienteColor = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return getBaseClienteColor(clienteId, cliente?.color);
  };

  const getGoogleCalUrl = (cita) => {
    const start = new Date(cita.fecha_hora);
    const end = new Date(start.getTime() + (cita.duracion_minutos || 60) * 60000);
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
    const clienteNombre = getClienteNombre(cita.cliente_id);
    const details = `Cliente: ${clienteNombre}${cita.modalidad ? `\nModalidad: ${cita.modalidad}` : ''}`;
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(cita.titulo || 'Cita')}&dates=${fmt(start)}/${fmt(end)}&details=${encodeURIComponent(details)}`;
  };

  const getEstadoColor = (estado) => {
    const colores = {
      programada: '#17a2b8',
      confirmada: '#28a745',
      cancelada: '#dc3545',
      completada: '#6c757d',
    };
    return colores[estado] || '#6c757d';
  };

  // Funciones para el calendario
  const getCitasFiltradas = () => {
    let filtradas = citas;
    
    // Filtro por estado
    if (filtroEstado !== '') {
      filtradas = filtradas.filter(cita => {
        const estadoCita = (cita.estado || '').toLowerCase();
        return estadoCita === filtroEstado.toLowerCase();
      });
    }
    
    // Filtro por búsqueda de texto
    if (busqueda.trim() !== '') {
      const query = busqueda.toLowerCase();
      filtradas = filtradas.filter(cita => {
        const titulo = (cita.titulo || '').toLowerCase();
        const descripcion = (cita.descripcion || '').toLowerCase();
        const clienteNombre = getClienteNombre(cita.cliente_id).toLowerCase();
        return titulo.includes(query) || descripcion.includes(query) || clienteNombre.includes(query);
      });
    }
    
    return filtradas;
  };

  const getCitasPorDia = (fecha) => {
    const citasFiltradas = getCitasFiltradas();
    // Usar métodos locales para evitar problemas de zona horaria
    const year = fecha.getFullYear();
    const month = fecha.getMonth();
    const day = fecha.getDate();
    
    return citasFiltradas.filter(cita => {
      const citaFecha = new Date(cita.fecha_hora);
      const citaYear = citaFecha.getFullYear();
      const citaMonth = citaFecha.getMonth();
      const citaDay = citaFecha.getDate();
      
      return citaYear === year && citaMonth === month && citaDay === day;
    });
  };

  const getDiasDelMes = () => {
    const year = mesActual.getFullYear();
    const month = mesActual.getMonth();
    const primerDia = new Date(year, month, 1);
    const ultimoDia = new Date(year, month + 1, 0);
    const diasEnMes = ultimoDia.getDate();
    // getDay(): 0=Domingo, 1=Lunes,... -> ajustamos para que 0=Lunes
    const diaInicioSemana = (primerDia.getDay() + 6) % 7;
    
    const dias = [];
    
    // Días del mes anterior para completar la primera semana
    const mesAnterior = new Date(year, month, 0);
    const diasMesAnterior = mesAnterior.getDate();
    for (let i = diaInicioSemana - 1; i >= 0; i--) {
      dias.push(new Date(year, month - 1, diasMesAnterior - i));
    }
    
    // Días del mes actual
    for (let i = 1; i <= diasEnMes; i++) {
      dias.push(new Date(year, month, i));
    }
    
    // Días del mes siguiente para completar la última semana
    const diasRestantes = 42 - dias.length; // 6 semanas * 7 días
    for (let i = 1; i <= diasRestantes; i++) {
      dias.push(new Date(year, month + 1, i));
    }
    
    return dias;
  };

  const cambiarMes = (direccion) => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + direccion, 1));
  };

  const esHoy = (fecha) => {
    const hoy = new Date();
    return fecha.toDateString() === hoy.toDateString();
  };

  const esMesActual = (fecha) => {
    return fecha.getMonth() === mesActual.getMonth() && fecha.getFullYear() === mesActual.getFullYear();
  };

  const handleCreate = (fecha = null) => {
    setEditingCita(null);
    setFechaInicial(fecha);
    setShowForm(true);
  };
  
  // Manejar clic en un día del calendario
  const handleDiaClick = (fecha) => {
    // Crear una nueva fecha con la fecha del día clickeado (sin hora, solo fecha)
    // Esto asegura que usamos la fecha correcta sin problemas de zona horaria
    // Usar los componentes directamente para evitar problemas de zona horaria
    const year = fecha.getFullYear();
    const month = fecha.getMonth();
    const day = fecha.getDate();
    const fechaSeleccionada = new Date(year, month, day);
    
    handleCreate(fechaSeleccionada);
  };

  const handleEdit = (cita) => {
    setEditingCita(cita);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta cita?')) {
      try {
        await citasAPI.delete(id);
        setSuccess('Cita eliminada correctamente');
        setShowForm(false);
        setEditingCita(null);
        loadCitas();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError('Error al eliminar la cita');
        console.error(err);
      }
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingCita(null);
    setOpenEmailModalOnMount(false);
    setFechaInicial(null);
  };

  const handleFormSubmit = async (data) => {
    try {
      if (videollamadasHabilitadas) {
        data.generar_sala_video = true;
      }
      if (editingCita) {
        await citasAPI.update(editingCita.id, data);
        setSuccess('Cita actualizada correctamente');
        setShowForm(false);
        setEditingCita(null);
      } else {
        const { enviarEmailAlCrear, ...dataToSend } = data;
        const res = await citasAPI.create(dataToSend);
        const newCita = res.data;
        const nSerie = res.data?.serie_creadas ?? 1;
        setSuccess(
          nSerie > 1
            ? `Se crearon ${nSerie} citas programadas correctamente.`
            : 'Cita creada correctamente'
        );
        if (enviarEmailAlCrear && newCita?.id) {
          setEditingCita(newCita);
          setOpenEmailModalOnMount(true);
        } else {
          setShowForm(false);
          setEditingCita(null);
        }
      }
      loadCitas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al guardar la cita. ' + (err.response?.data?.error || ''));
      console.error(err);
    }
  };

  const handleEventDrop = async (cita, newStart, revertFn) => {
    const pad = (n) => String(n).padStart(2, '0');
    const newFechaHora = `${newStart.getFullYear()}-${pad(newStart.getMonth() + 1)}-${pad(newStart.getDate())} ${pad(newStart.getHours())}:${pad(newStart.getMinutes())}:00`;
    try {
      const updateData = { ...cita, fecha_hora: newFechaHora };
      if (videollamadasHabilitadas) updateData.generar_sala_video = true;
      await citasAPI.update(cita.id, updateData);
      setSuccess('Cita movida correctamente');
      loadCitas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al mover la cita');
      revertFn();
    }
  };

  const calendaroVisualHabilitado = currentUser?.calendario_visual_habilitado === true || currentUser?.calendario_visual_habilitado === 1;
  const citasGrupalesHabilitadas = currentUser?.citas_grupales_habilitadas === true || currentUser?.citas_grupales_habilitadas === 1;
  const videollamadasHabilitadas = currentUser?.rol === 'admin' || currentUser?.videollamadas_habilitadas === 1 || currentUser?.videollamadas_habilitadas === true;

  const getParticipantesNombres = (cita) => {
    if (!cita.es_grupal || !cita.participantes || cita.participantes.length === 0) {
      return getClienteNombre(cita.cliente_id);
    }
    return cita.participantes.map(p => `${p.nombre} ${p.apellidos}`).join(', ');
  };

  if (loading) {
    return <div className="loading">Cargando citas...</div>;
  }

  return (
    <div className="citas">
      <div className="page-header">
        <h2>Agenda</h2>
        <div className="header-actions">
          <div className="view-toggle">
            {calendaroVisualHabilitado && (
              <button
                className={`btn ${vistaFullCalendar ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => { setVistaFullCalendar(true); setVistaCalendario(true); }}
              >
                📅 Calendario Pro
              </button>
            )}
            <button
              className={`btn ${vistaCalendario && !vistaFullCalendar ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setVistaCalendario(true); setVistaFullCalendar(false); }}
            >
              📅 Calendario
            </button>
            <button
              className={`btn ${!vistaCalendario ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setVistaCalendario(false); setVistaFullCalendar(false); }}
            >
              📋 Lista
            </button>
          </div>
          <button className="btn btn-secondary" onClick={() => exportToCSV(getCitasFiltradas(), [
            { key: 'titulo', label: 'Título' },
            { label: 'Cliente', format: (r) => getParticipantesNombres(r) },
            { label: 'Fecha', format: (r) => r.fecha_hora ? new Date(r.fecha_hora).toLocaleDateString('es-ES') : '' },
            { label: 'Hora', format: (r) => r.fecha_hora ? new Date(r.fecha_hora).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '' },
            { key: 'duracion_minutos', label: 'Duración (min)' },
            { key: 'estado', label: 'Estado' },
            { key: 'modalidad', label: 'Modalidad' },
            { key: 'descripcion', label: 'Descripción' },
          ], 'citas')}>
            📥 Exportar CSV
          </button>
          <button className="btn btn-primary" onClick={handleCreate}>
            Nueva Cita
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="filtros">
        <div className="filtros-grid">
          <div className="form-group">
            <label>🔍 Buscar:</label>
            <input
              type="text"
              placeholder="Buscar por título, comentarios..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Filtrar por Cliente:</label>
            <select
              value={filtroCliente}
              onChange={(e) => setFiltroCliente(e.target.value)}
            >
              <option value="">Todos los clientes</option>
              {clientes.map(cliente => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nombre} {cliente.apellidos}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Filtrar por Estado:</label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
            >
              <option value="">Todos los estados</option>
              <option value="programada">Programada</option>
              <option value="confirmada">Confirmada</option>
              <option value="cancelada">Cancelada</option>
              <option value="completada">Completada</option>
            </select>
          </div>
          <div className="form-group">
            <label>Fecha Inicio:</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Fecha Fin:</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </div>
        </div>
      </div>

      {vistaFullCalendar && calendaroVisualHabilitado ? (
        <Suspense fallback={<div className="loading">Cargando calendario...</div>}>
          <CalendarioVisual
            citas={getCitasFiltradas()}
            clientes={clientes}
            getClienteColor={getClienteColor}
            getClienteNombre={getClienteNombre}
            onEventClick={handleEdit}
            onDateClick={(date) => handleDiaClick(date)}
            onEventDrop={handleEventDrop}
          />
        </Suspense>
      ) : vistaCalendario ? (
        <div className="calendario-container">
          <div className="calendario-header">
            <button className="btn btn-secondary" onClick={() => cambiarMes(-1)}>
              ← Mes Anterior
            </button>
            <h3 className="mes-titulo">
              {mesActual.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }).replace(' de ', ' ')}
            </h3>
            <button className="btn btn-secondary" onClick={() => cambiarMes(1)}>
              Mes Siguiente →
            </button>
          </div>
          <div className="calendario-grid">
            <div className="calendario-dias-semana">
              <div className="dia-semana">Lun</div>
              <div className="dia-semana">Mar</div>
              <div className="dia-semana">Mié</div>
              <div className="dia-semana">Jue</div>
              <div className="dia-semana">Vie</div>
              <div className="dia-semana">Sáb</div>
              <div className="dia-semana">Dom</div>
            </div>
            <div className="calendario-dias">
              {getDiasDelMes().map((fecha, index) => {
                const citasDia = getCitasPorDia(fecha);
                const esDiaActual = esHoy(fecha);
                const esDelMes = esMesActual(fecha);
                
                return (
                  <div
                    key={index}
                    className={`calendario-dia ${!esDelMes ? 'otro-mes' : ''} ${esDiaActual ? 'hoy' : ''}`}
                    onClick={(e) => {
                      // Solo crear cita si se hace clic en el día (no en una cita existente)
                      if (e.target === e.currentTarget || e.target.classList.contains('dia-numero') || e.target.classList.contains('dia-citas') || e.target.classList.contains('cita-mas')) {
                        handleDiaClick(fecha);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Haz clic para crear una nueva cita"
                  >
                    <div className="dia-numero">{fecha.getDate()}</div>
                    <div className="dia-citas">
                      {citasDia.slice(0, 3).map(cita => {
                        const fechaHora = new Date(cita.fecha_hora);
                        const estadoCita = (cita.estado || '').toLowerCase();

                        // Estilo específico para sesiones canceladas en el calendario
                        const esCancelada = estadoCita === 'cancelada';
                        const estiloCitaMini = esCancelada
                          ? {
                              backgroundColor: '#e9ecef', // gris clarito
                              color: '#6c757d',
                              textDecoration: 'line-through',
                              border: '1px dashed #ced4da',
                            }
                          : {
                              backgroundColor: getClienteColor(cita.cliente_id),
                            };

                        return (
                          <div
                            key={cita.id}
                            className="cita-mini"
                            style={estiloCitaMini}
                            onClick={(e) => {
                              e.stopPropagation(); // Evitar que se active el clic del día
                              handleEdit(cita);
                            }}
                            title={`${cita.titulo} - ${getParticipantesNombres(cita)} - ${fechaHora.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`}
                          >
                            <span className="cita-mini-hora">
                              {fechaHora.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className="cita-mini-titulo">{cita.titulo}</span>
                          </div>
                        );
                      })}
                      {citasDia.length > 3 && (
                        <div 
                          className="cita-mas"
                          onClick={(e) => {
                            e.stopPropagation(); // Evitar que se active el clic del día
                          }}
                        >+{citasDia.length - 3} más</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="citas-list">
          {getCitasFiltradas().map(cita => {
            const fechaHora = new Date(cita.fecha_hora);
            const hoyInicio = new Date(); hoyInicio.setHours(0,0,0,0);
            const fechaCitaDia = new Date(fechaHora); fechaCitaDia.setHours(0,0,0,0);
            const esCitaPasada = fechaCitaDia < hoyInicio;
            const clienteColor = getClienteColor(cita.cliente_id);
            return (
              <div key={cita.id} id={`cita-${cita.id}`} className="cita-card" style={{ borderLeftColor: clienteColor }}>
                <div className="cita-header">
                  <div>
                    <h3>{cita.titulo}</h3>
                    {cita.numero_sesion && (
                      <p className="cita-numero-sesion">Sesión #{cita.numero_sesion}</p>
                    )}
                    <p className="cita-cliente">
                      {cita.es_grupal ? (
                        <><span style={{ fontSize: '12px', background: '#e3f2fd', color: '#1565c0', padding: '1px 6px', borderRadius: '4px', marginRight: '6px' }}>Grupal</span>{getParticipantesNombres(cita)}</>
                      ) : getClienteNombre(cita.cliente_id)}
                    </p>
                    <p className="cita-modalidad">Modalidad: {cita.modalidad || 'Presencial'}</p>
                    <p className="cita-fecha">
                      {fechaHora.toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'long'
                      })} a las {fechaHora.toLocaleTimeString('es-ES', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="cita-info">
                    <span
                      className="cita-estado"
                      style={{ backgroundColor: getEstadoColor(cita.estado) }}
                    >
                      {cita.estado}
                    </span>
                    <span className="cita-duracion">{cita.duracion_minutos} min</span>
                  </div>
                </div>
                {cita.descripcion && (
                  <div className="cita-descripcion">
                    <div dangerouslySetInnerHTML={safeHtml(cita.descripcion)} />
                  </div>
                )}
                <div className="cita-actions">
                  <button className="btn btn-edit btn-sm" onClick={() => handleEdit(cita)}>
                    Editar
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(cita.id)}>
                    Eliminar
                  </button>
                  {(currentUser?.rol === 'admin' || currentUser?.whatsapp_habilitado === true || currentUser?.whatsapp_habilitado === 1) && (() => {
                    const cliente = clientes.find(c => c.id === cita.cliente_id);
                    const phone = cliente?.whatsapp || cliente?.telefono || '';
                    if (!phone) return null;
                    return (
                      <button
                        className="btn btn-sm"
                        style={{ background: '#25D366', color: '#fff', border: 'none' }}
                        onClick={() => {
                          const cleanPhone = phone.replace(/[^0-9]/g, '');
                          const fh = new Date(cita.fecha_hora);
                          const fechaStr = fh.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                          const horaStr = fh.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                          const nombre = cliente ? `${cliente.nombre || ''} ${cliente.apellidos || ''}`.trim() : '';
                          const texto = `Hola ${nombre}, te recuerdo tu cita "${cita.titulo}" el ${fechaStr} a las ${horaStr}. Modalidad: ${cita.modalidad || 'Presencial'}. Duración: ${cita.duracion_minutos} min.`;
                          window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(texto)}`, '_blank');
                        }}
                        title="Enviar recordatorio por WhatsApp"
                      >
                        WhatsApp
                      </button>
                    );
                  })()}
                  <a
                    href={getGoogleCalUrl(cita)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm"
                    style={{ background: '#4285F4', color: '#fff', border: 'none', textDecoration: 'none' }}
                    title="Añadir a Google Calendar"
                  >
                    G Cal
                  </a>
                  {cita.sala_video_token ? (
                    esCitaPasada ? (
                      <span
                        className="btn btn-sm"
                        style={{ background: '#e9ecef', color: '#adb5bd', border: '1px solid #dee2e6', cursor: 'default', textDecoration: 'none', fontSize: '12px' }}
                        title="Videollamada no disponible para sesiones pasadas"
                      >
                        📹 Videollamada
                      </span>
                    ) : (
                      <a
                        href={`/videollamada/${cita.sala_video_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ background: '#7c3aed', color: '#fff', border: 'none', textDecoration: 'none' }}
                        title="Iniciar videollamada"
                      >
                        📹 Videollamada
                      </a>
                    )
                  ) : (cita.modalidad || '').toLowerCase() === 'online' && (
                    videollamadasHabilitadas ? (
                      esCitaPasada ? (
                        <span
                          className="btn btn-sm"
                          style={{ background: '#e9ecef', color: '#adb5bd', border: '1px solid #dee2e6', cursor: 'default', textDecoration: 'none', fontSize: '12px' }}
                          title="No se puede generar enlace para sesiones pasadas"
                        >
                          📹 Generar enlace
                        </span>
                      ) : (
                        <button
                          className="btn btn-sm"
                          style={{ background: '#7c3aed', color: '#fff', border: 'none' }}
                          title="Generar enlace de videollamada"
                          onClick={async () => {
                            try {
                              await citasAPI.update(cita.id, { ...cita, generar_sala_video: true });
                              setSuccess('Enlace de videollamada generado');
                              loadCitas();
                              setTimeout(() => setSuccess(null), 3000);
                            } catch (err) {
                              setError('Error al generar enlace');
                            }
                          }}
                        >
                          📹 Generar enlace
                        </button>
                      )
                    ) : null
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {citas.length === 0 && (
        <div className="empty-state">
          <p>No hay citas. Crea la primera haciendo clic en "Nueva Cita".</p>
        </div>
      )}

      {showForm && (
        <CitaForm
          cita={editingCita}
          clientes={clientes}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          onDelete={editingCita ? () => handleDelete(editingCita.id) : null}
          fechaInicial={fechaInicial}
          openEmailModalOnMount={openEmailModalOnMount}
          onOpenEmailModalShown={() => setOpenEmailModalOnMount(false)}
          citasGrupalesHabilitadas={citasGrupalesHabilitadas}
          videollamadasHabilitadas={videollamadasHabilitadas}
        />
      )}
    </div>
  );
}

export default Citas;

